import { Badge, useTheme } from "@deere/fuel-react";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { DealerAccountContext } from "../../../../../context/DealerAccountContext";
import { RBACAccessContext } from "../../../../../context/RBACAccessContext/RBACAccessContext";
import ResourceLink from "../../../../orders/components/ResourceLink/ResourceLink";
import { D2DOrderRow } from "../D2DActionMenu/D2DActionMenu";

interface D2DAcceptOrderModalProps {
  open: boolean;
  request: D2DOrderRow | null;
  onClose: () => void;
  onAccept: (
    request: D2DOrderRow,
    acceptedQuantity: number
  ) => void | Promise<void>;
}

const D2DAcceptOrderModal = ({
  open,
  request,
  onClose,
  onAccept,
}: D2DAcceptOrderModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d"]);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const { dealerAccount } = useContext(DealerAccountContext);

  if (!request) {
    return null;
  }

  const encodedPartNumber = encodeURIComponent(request.partNumber);
  // /parts/:partNumber is only routed when RBAC is on; otherwise use the dealer-scoped route
  const partInfoUrl = isRBACEnabled
    ? `/parts/${encodedPartNumber}`
    : `/dealer/${dealerAccount?.id}/parts/${encodedPartNumber}`;

  const handleAccept = async () => {
    await onAccept(request, request.requestedQuantity);
  };

  const closeLabel = t("d2d:d2dRequests.acceptModal.close", "Close") ?? "Close";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="d2d-accept-order-title"
      fullWidth
      maxWidth="sm"
      data-testid="d2d-accept-order-modal"
    >
      <DialogTitle
        id="d2d-accept-order-title"
        data-testid="d2d-accept-modal-title"
      >
        {t("d2d:d2dRequests.acceptOrderTitle", {
          defaultValue: "Accept Order {{orderNumber}}",
          orderNumber: request.orderNumber,
        })}
      </DialogTitle>
      <IconButton
        aria-label={closeLabel}
        onClick={onClose}
        size="large"
        sx={{
          position: "absolute",
          right: 15,
          top: 15,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: theme.tokens.FuelSpacing10,
            rowGap: theme.tokens.FuelSpacing6,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                color: theme.tokens.FuelColorTextPrimary,
                mb: theme.tokens.FuelSpacing1,
              }}
            >
              {t(
                "d2d:d2dRequests.acceptModal.dealerReference",
                "Dealer reference"
              )}
            </Typography>
            <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
              {request.dealerReference}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                color: theme.tokens.FuelColorTextPrimary,
                mb: theme.tokens.FuelSpacing1,
              }}
            >
              {t("d2d:d2dRequests.acceptModal.orderStatus", "Order status")}
            </Typography>
            <Badge
              startIcon={<StarIcon />}
              variant="info"
              data-testid="d2d-accept-status-badge"
            >
              {t("d2d:status.new", "New")}
            </Badge>
          </Box>

          <Box sx={{ gridColumn: "1 / span 2" }}>
            <Typography
              sx={{
                fontWeight: theme.tokens.FuelFontWeightSemibold,
                color: theme.tokens.FuelColorTextPrimary,
                mb: theme.tokens.FuelSpacing2,
              }}
            >
              {t("d2d:d2dRequests.acceptModal.orderItems", "Order item")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr",
                columnGap: theme.tokens.FuelSpacing10,
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t(
                    "d2d:d2dRequests.acceptModal.partInformation",
                    "Part information"
                  )}
                </Typography>
                <Box data-testid="d2d-part-information">
                  <ResourceLink value={request.partNumber} url={partInfoUrl} />
                </Box>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    color: theme.tokens.FuelColorTextPrimary,
                    mb: theme.tokens.FuelSpacing1,
                  }}
                >
                  {t(
                    "d2d:d2dRequests.acceptModal.requestedQuantity",
                    "Requested Quantity"
                  )}
                </Typography>
                <Typography sx={{ color: theme.tokens.FuelColorTextSecondary }}>
                  {request.requestedQuantity}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="secondary"
          onClick={onClose}
          data-testid="d2d-accept-cancel"
        >
          {t("d2d:d2dRequests.acceptModal.cancel", "Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleAccept}
          data-testid="d2d-accept-confirm"
        >
          {t("d2d:d2dRequests.acceptModal.confirm", "Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default D2DAcceptOrderModal;
